import React, { useState } from 'react';
import { motion } from 'framer-motion';
import '../../styles/product-card.css';
import { Col } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions } from '../../redux/slices/cartSlice';
import { wishlistActions } from '../../redux/slices/wishlistSlice';
import useAuth from '../../custom-hooks/useAuth';
import QuickViewModal from './QuickViewModal';
import { getProductImg, handleImgError } from '../../utils/imageHelper';

const materialMap = {
  sofa: 'Kiln-Dried Hardwood & Velvet',
  chair: 'Solid European Ash Wood',
  watch: 'Stainless Steel & Sapphire Crystal',
  mobile: 'Aerospace Aluminium & Ceramic',
  wireless: 'Matte Polymer & Memory Foam',
};

const ProductCard = ({ item }) => {
  const [showQuickView, setShowQuickView] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const isWishlisted = wishlistItems.some((w) => w.id === item.id);

  const stockCount = item.stockQuantity !== undefined ? item.stockQuantity : item.stock !== undefined ? item.stock : 14;
  const isOutOfStock = item.inStock === false || stockCount <= 0;

  const addToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isOutOfStock) {
      toast.warning('This piece is currently fully committed / out of stock.');
      return;
    }

    if (!currentUser) {
      toast.info('Please sign in to save your bag and pieces to your account.', {
        autoClose: 3000,
      });
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    dispatch(
      cartActions.addItem({
        id: item.id,
        productName: item.productName,
        price: item.price,
        imgUrl: item.imgUrl,
      })
    );
    toast.success('Piece added to shopping bag');
  };

  const toggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      toast.info('Please sign in to save pieces to your wishlist.', {
        autoClose: 3000,
      });
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    dispatch(wishlistActions.toggleWishlist(item));
    if (isWishlisted) {
      toast.info('Removed from wishlist');
    } else {
      toast.success('Saved to wishlist');
    }
  };

  const originalPrice = Math.round(item.price * 1.25);
  const materialTag = item.material || materialMap[item.category] || 'Artisanal Handcrafted';
  const ratingValue = item.avgRating || 4.7;
  const reviewCount = item.reviews?.length || 8;

  // Determine badge based on item ID or price
  const isBestseller = parseInt(item.id, 10) % 2 === 1;

  return (
    <>
      <Col lg="3" md="4" sm="6" className="mb-4">
        <div className="product__item">
          {/* Top Badges & Actions */}
          <div className="product__badge-wrapper">
            <span className={`product__badge ${isBestseller ? 'bestseller' : 'new'}`}>
              {isBestseller ? 'BESTSELLER' : 'NEW EDITION'}
            </span>

            <button
              type="button"
              className={`product__wishlist-btn ${isWishlisted ? 'active' : ''}`}
              onClick={toggleWishlist}
              title={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
              aria-label="Toggle wishlist"
            >
              <i className={isWishlisted ? 'ri-heart-fill' : 'ri-heart-line'}></i>
            </button>
          </div>

          {/* Product Image Container */}
          <div className="product__img">
            <Link to={`/shop/${item.id}`} aria-label={item.productName}>
              <img
                src={getProductImg(item)}
                onError={(e) => handleImgError(e, item)}
                alt={item.productName}
                className="product-img"
                width="260"
                height="175"
                loading="lazy"
                decoding="async"
              />
            </Link>

            {/* Quick View Button overlay on hover */}
            <button
              type="button"
              className="product__quickview-trigger"
              onClick={() => setShowQuickView(true)}
              title="Quick Preview"
            >
              <i className="ri-eye-line me-1"></i> Quick View
            </button>
          </div>

          {/* Product Info & Specifications */}
          <div className="product__info">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="product__category">{item.category}</span>
              <div className="product__rating-chip">
                <i className="ri-star-fill"></i>
                <span>{ratingValue} ({reviewCount})</span>
              </div>
            </div>

            <h3 className="product__name">
              <Link to={`/shop/${item.id}`}>{item.productName}</Link>
            </h3>

            <div className="d-flex align-items-center justify-content-between mt-1 mb-2">
              <span className="product__material-spec">{materialTag}</span>
              {isOutOfStock ? (
                <span className="badge bg-danger" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                  Out of Stock
                </span>
              ) : stockCount <= 5 ? (
                <span className="badge bg-warning text-dark" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                  Only {stockCount} left
                </span>
              ) : (
                <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {stockCount} in stock
                </span>
              )}
            </div>
          </div>

          {/* Price & Add to Cart Footer */}
          <div className="product__card-bottom d-flex align-items-center justify-content-between">
            <div className="product__pricing">
              <span className="price">${item.price}</span>
              <span className="price__original">${originalPrice}</span>
            </div>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={addToCart}
              className={`product__add-btn ${isOutOfStock ? 'disabled opacity-50' : ''}`}
              title={isOutOfStock ? 'Piece is currently out of stock' : 'Add to Shopping Bag'}
              type="button"
              aria-label="Add to cart"
            >
              <i className={isOutOfStock ? 'ri-close-line' : 'ri-shopping-bag-line'}></i>
            </motion.button>
          </div>
        </div>
      </Col>

      {/* Quick View Modal */}
      <QuickViewModal
        product={item}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </>
  );
};

export default ProductCard;